import { Badge } from "@/components/ui/badge";
// `@medusajs/ui`'s tooltip, not the app's `components/ui/tooltip` - the two
// sit on different Radix packages (`radix-ui` vs `@radix-ui/react-tooltip`),
// so they don't share a provider context, and the one mounted app-wide in
// `providers.tsx` is Medusa's. Same choice `TruncateTooltip` makes.
import { Tooltip } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { memo } from "react";

type StoreLiveProvenance = SalonStockerHttpTypes.StoreLiveProvenance;

/**
 * What the badge is vouching for, used to phrase its tooltip. Every surface
 * naming its own subject is what keeps "Estimated" from reading as a blanket
 * disclaimer on the page - the marker beside a shipping row is about that
 * rate, not about the prices above it.
 */
export type LiveProvenanceSubject = "price" | "shipping rate" | "total";

export interface LiveProvenanceBadgeProps {
  /**
   * The value's provenance, straight off the API payload.
   *
   * Tri-state, and the third state is `undefined`: the server omits this
   * field entirely for internal/external sellers and for a direct-integration
   * seller whose connection is down, so "no field" means "no marker" and this
   * component renders nothing (KAN-105 AC5). That is the whole reason the
   * gating lives on the server - no caller has to know a seller's checkout
   * style to decide whether a marker belongs.
   */
  provenance?: StoreLiveProvenance;
  /** What the marker is about, for the tooltip wording. */
  subject?: LiveProvenanceSubject;
  /**
   * Who the value came from, named in the tooltip. Usually a supplier's name;
   * a cross-seller roll-up passes a phrase instead ("your suppliers"), since
   * it speaks for several at once. Defaults to "the supplier".
   */
  source?: string;
  /**
   * Compact treatment for dense money rows (line items, inline totals), where
   * the full badge padding would out-weigh the price it annotates.
   */
  size?: "sm" | "md";
  className?: string;
}

/** The tooltip / screen-reader sentence for a shown marker. */
export function describeProvenance(
  provenance: StoreLiveProvenance,
  subject: LiveProvenanceSubject,
  source = "the supplier"
): string {
  if (provenance === "live") {
    return `Live ${subject} from ${source}, fetched just now`;
  }

  // A total is never fetched as such - it is summed from values that were,
  // so the honest fallback sentence for it is about its parts, not about a
  // call that failed.
  if (subject === "total") {
    return "Estimated total - at least one amount in it isn't live, so SalonStocker's own figures are included";
  }

  // Source-free on purpose: the buyer's question here is "why isn't this
  // live", and the answer is the same whoever it was meant to come from.
  // Named by subject rather than "live pricing", so a shipping rate that fell
  // open doesn't report itself as a pricing failure.
  return `Estimated ${subject} - no live ${subject} came back, so SalonStocker's own figure is shown`;
}

/**
 * The buyer-facing "this number came straight from the supplier" marker
 * (KAN-105): green **Live** when the exact value beside it was resolved from
 * a successful live call on this request, neutral grey **Estimated** when
 * that call fell open to SalonStocker's own figure, and nothing at all when
 * the question doesn't apply.
 *
 * Renders `null` rather than a placeholder for that third case. A cart mixing
 * a direct-integration supplier with an internal one should mark only the
 * rows the marker means something for; a grey chip on every other row would
 * turn a trust signal into page furniture.
 */
export const LiveProvenanceBadge = memo(function LiveProvenanceBadge({
  provenance,
  subject = "price",
  source,
  size = "sm",
  className
}: LiveProvenanceBadgeProps) {
  if (!provenance) return null;

  const isLive = provenance === "live";
  const description = describeProvenance(provenance, subject, source);
  const isSm = size === "sm";

  return (
    <Tooltip content={description} side="top">
      <Badge
        variant={isLive ? "success" : "neutral"}
        // Radix's `asChild` trigger attaches focus handlers but does not make
        // the child focusable, and `Badge` is a `div` - so without this the
        // tooltip opens on hover only, and a sighted keyboard-only user (who
        // gets no screen-reader text either) has no way to reach the
        // explanation at all.
        tabIndex={0}
        className={cn(
          "gap-1 whitespace-nowrap font-medium",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1",
          isSm ? "px-1.5 py-0 text-[10px]" : "px-2 py-0.5 text-xs",
          className
        )}
        data-testid="live-provenance-badge"
        data-provenance={provenance}
      >
        <span
          className={cn(
            "inline-block flex-shrink-0 rounded-full",
            isSm ? "h-1.5 w-1.5" : "h-2 w-2",
            isLive ? "bg-green-600" : "bg-gray-400"
          )}
          aria-hidden="true"
        />
        <span aria-hidden="true">{isLive ? "Live" : "Estimated"}</span>
        {/* The visible word alone ("Live") doesn't say live *what*, and the
            tooltip only reaches a pointer - a phone can't hover it at all.
            The full sentence is what a screen reader gets, in place of the
            two-word label.

            Radix also sets `aria-describedby` to the same sentence while the
            tooltip is open, so a screen reader hears it twice in that one
            state. Kept anyway: the alternative is naming the badge "Live" and
            leaving the description to the tooltip, which says nothing at all
            in the closed state - which is every state, for a reader that
            never opens it. */}
        <span className="sr-only">{description}</span>
      </Badge>
    </Tooltip>
  );
});
